import { message, Typography } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ScrollPanel from "../../../components/ScrollPanel";

import SplitPane from "react-split-pane";

import _ from "lodash";
import { usePostHog } from "@posthog/react";
import { getUroSDK, getUroSocket } from "../../../services/URO";
import colors from "../../../utils/theme/colors";
import ImageJobsList from "./components/imageJobsList/ImageJobsList";
import ModelConfigCol from "./components/modelConfigCol/ModelConfigCol";
import { useLocation } from "react-router";

export default function PlaygroundScreen() {
  const location = useLocation();
  const posthog = usePostHog();
  const fetchSeqRef = useRef(0); // guard to avoid stale search results overwriting new ones
  const [models, setModels] = useState({
    loading: true,
    _error: null,
    data: [],
    pagination: {},
    params: {
      model_name: "",
      model_description: "",
    },
  });
  const [model, setModel] = useState(null);
  const [imageJobs, setImageJobs] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });

  const handleGenerateImage = async (params) => {
    try {
      await getUroSDK().runModel({
        model_id: params.model_id,
        ...params.params,
      });

      // PostHog: Capture model run submission
      posthog?.capture("model_run_submitted", {
        model_id: params.model_id,
        model_name: model?.model_name,
        model_type: model?.model_type,
      });

      message.success("Image job submitted");
    } catch (error) {
      console.error('Failed to generate image:', error?.message);
      posthog?.capture("model_run_failed", {
        model_id: params.model_id,
        error: error?.message,
      });
      message.error("Failed to generate image");
    }
  };

  const fetchModels = useCallback(async (params) => {
    try {
      const seq = ++fetchSeqRef.current;
      setModels((prev) => ({ ...prev, loading: true, _error: null }));
      const reqBase = {
        limit: params?.limit || 100,
        page: params?.page || 1,
      };
      const name = (params?.model_name ?? '').trim();
      const desc = (params?.model_description ?? '').trim();
      const req = { ...reqBase };
      if (name) {
        req.model_name = name;
        // Include common synonyms to be safe if backend expects different key
        req.name = name;
        req.search = name;
        req.query = name;
        req.q = name;
      }
      if (desc) req.model_description = desc;
      await getUroSDK()
        .listModels(req)
        .then(({ data }) => {
          // ignore stale responses
          if (seq !== fetchSeqRef.current) {
            return;
          }
          // Normalize response shape
          const payload = data;
          let items = [];
          if (Array.isArray(payload)) items = payload;
          else if (Array.isArray(payload?.items)) items = payload.items;
          else if (Array.isArray(payload?.models)) items = payload.models;
          else if (Array.isArray(payload?.data)) items = payload.data;
          else if (Array.isArray(payload?.results)) items = payload.results;
          // Fallback client-side filtering if backend doesn't apply search
          const searchTerm = (name || '').toLowerCase();
          if (searchTerm) {
            items = items.filter((it) => {
              const n = (it?.model_name || '').toLowerCase();
              const d = (it?.model_description || '').toLowerCase();
              return n.includes(searchTerm) || d.includes(searchTerm);
            });
          }
          const page = payload?.page ?? payload?.pagination?.page ?? 1;
          const limit = payload?.limit ?? payload?.pagination?.limit ?? reqBase.limit;
          const totalItems = payload?.totalItems ?? payload?.pagination?.totalItems ?? items.length;
          setModels((prev) => ({
            ...prev,
            loading: false,
            data: items,
            pagination: {
              page,
              limit,
              total: totalItems,
            },
            // Persist last-used search/filter params for children (ModelConfigCol) to read
            params: {
              model_name: params?.model_name || '',
              model_description: params?.model_description || '',
            },
          }));
        });
    } catch (error) {
      console.error('[fetchModels] error:', error);
      setModels((prev) => ({ ...prev, _error: error }));
      message.error("Failed to fetch models");
    } finally {
      setModels((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  const fetchImageJobs = useCallback(
    async (page = 1, limit = 10, append = false) => {
      try {
        const { data } = await getUroSDK().listImageJobs({ page, limit });
        if (append) {
          // For infinite scroll - append new items
          setImageJobs((prev) => {
            const newItems = data.items || [];
            if (newItems.length === 0) return prev;
            return [...prev, ...newItems];
          });
          // Update pagination to reflect the new current page
          setPagination(prev => ({
            page: data.pagination.page,
            limit: data.pagination.limit,
            totalItems: data.pagination.totalItems,
            totalPages: data.pagination.totalPages,
          }));
        } else {
          // For initial load or page change - replace items
          setImageJobs(data.items);
          setPagination({
            page: data.pagination.page,
            limit: data.pagination.limit,
            totalItems: data.pagination.totalItems,
            totalPages: data.pagination.totalPages,
          });
        }
      } catch (error) {
        console.error('Failed to fetch image jobs:', error?.message);
        message.error("Failed to fetch image jobs");
      }
    },
    []
  );

  const addOrUpdateJob = useCallback((job) => {
    setImageJobs((prev) => {
      const existingJob = _.find(prev, { image_job_id: job.image_job_id });
      if (existingJob) {
        return prev.map((j) => (j.image_job_id === job.image_job_id ? job : j));
      } else {
        return [job, ...prev];
      }
    });
  }, []);

  const fetchModel = useCallback(async (id) => {
    try {
      const { data } = await getUroSDK().getModelById(id);
      return data;
    } catch (error) {
      console.error('Failed to fetch model:', error?.message);
      message.error("Failed to fetch model");
      return null;
    }
  }, []);

  const handleModelSelect = (selectedModel) => {
    // PostHog: Capture model selection
    posthog?.capture("model_selected", {
      model_id: selectedModel.model_id,
      model_name: selectedModel.model_name,
      model_type: selectedModel.model_type,
    });

    fetchModel(selectedModel.model_id).then((m) => setModel(m));
  };

  const handleImageJobsPageChange = (page, pageSize) => {
    fetchImageJobs(page, pageSize);
  };

  const handleLoadMore = (page) => {
    fetchImageJobs(page, pagination.limit, true); // append = true for infinite scroll
  };

  // If navigated with ?model_id=, preselect that model automatically
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const preselectId = params.get("model_id");
      if (preselectId) {
        fetchModel(preselectId).then((m) => setModel(m));
      }
    } catch (e) {
      // ignore
    }
    // Only re-run if query string changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    fetchModels(models.params);
    fetchImageJobs(1, pagination.limit); // Initial fetch

    const socket = getUroSocket();
    const onCreated = (data) => {
      addOrUpdateJob({ ...data, status: "pending" });
    };
    const onProcessing = (data) => {
      addOrUpdateJob({ ...data, status: "processing" });
    };
    const onComplete = (data) => {
      addOrUpdateJob({
        ...data,
        status: "completed",
        cost_to_client: data.cost_to_client,
        results: data.results,
        success: data.success,
      });
    };
    const onError = (data) => {
      addOrUpdateJob({ ...data, status: "failed", error: data.error });
    };

    socket.on("job:created", onCreated);
    socket.on("job:processing", onProcessing);
    socket.on("job:complete", onComplete);
    socket.on("job:error", onError);

    return () => {
      if (socket && socket.off) {
        socket.off("job:created", onCreated);
        socket.off("job:processing", onProcessing);
        socket.off("job:complete", onComplete);
        socket.off("job:error", onError);
      }
      // Intentionally do not disconnect the shared socket here; AppLayout manages socket lifecycle.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchModels, addOrUpdateJob]);

  if (models._error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography.Text type="danger">{models._error.message}</Typography.Text>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Page header removed: balance moved to global header in AppLayout */}

      <ScrollPanel
        style={{
          flex: 1,
          height: "100%",
          width: "100%",
          minWidth: 0,
          minHeight: 0,
          background: "transparent",
          overflow: "hidden",
          overscrollBehavior: "none",
          boxSizing: "border-box",
        }}
      >
        <SplitPane
          split="vertical"
          defaultSize="70%"
          minSize={0}
          maxSize={-200}
          paneStyle={{
            display: "flex",
            alignItems: "stretch",
            minWidth: 0,
            height: "100%",
          }}
          style={{
            height: "100%",
            width: "100%",
            minWidth: 0,
            background: "transparent",
            boxSizing: "border-box",
          }}
          resizerStyle={{
            background: colors.background,
            width: 8,
            marginInline: 1,
            cursor: "col-resize",
            zIndex: 2,
          }}
        >
          {/* Left Panel: ModelConfigCol - Expanded center */}
          <div
            style={{
              background: "#0a0a0a",
              padding: 20,
              borderRadius: 12,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              overflow: "hidden",
              flex: 1,
              minHeight: 0,
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            }}
          >
            <ModelConfigCol
              handleGenerateImage={handleGenerateImage}
              modelsState={models}
              model={model}
              fetchModels={fetchModels}
              onModelSelect={handleModelSelect}
            />
          </div>
          {/* Right Panel: ImageJobsList - Darkened sidebar */}
          <div
            style={{
              background: "#0a0a0a",
              padding: 16,
              display: "flex",
              flexDirection: "column",
              borderRadius: 8,
              minWidth: 200,
              width: "100%",
              boxSizing: "border-box",
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 8,
              }}
            ></div>
            <ImageJobsList
              imageJobs={imageJobs}
              pagination={pagination}
              onPageChange={handleImageJobsPageChange}
              onLoadMore={handleLoadMore}
            />
          </div>
        </SplitPane>
      </ScrollPanel>
    </div>
  );
}
